import { PropsWithChildren, useState } from 'react'
import {
  Button,
  ButtonGroup,
  Column,
  Heading,
  InputCode,
  Modal,
  ModalBody,
  ModalFooter,
} from '@skedulo/breeze-ui-react'
import { hasPermission } from '../../index'
import { ErrorLevel, PagesPermission } from '../constants'
import { PlatformError } from '../utils'
import { ErrorDetail } from './error-detail'
import type { ErrorFallbackProps } from './error-fallback'

/**
 * Renders a dismissible alert dialog, which shows error details.
 */
export const ErrorDialog = ({
  code,
  traceId,
  error,
  reset,
  children,
}: PropsWithChildren<ErrorFallbackProps<ErrorLevel.TEMPLATE>>) => {
  const hasEnhancedErrorsPermission = hasPermission(
    PagesPermission.ENHANCED_ERRORS,
  )
  const [errorDetails, setErrorDetails] = useState<PlatformError | null>(error)

  const handleDismiss = () => {
    setErrorDetails(null)
    reset()
  }

  return (
    <Modal open={!!errorDetails} size="medium" onClose={handleDismiss}>
      <ModalBody>
        <Column>
          <Heading size="base">An unexpected error was encountered</Heading>

          <div>{children}</div>

          <div>
            <ErrorDetail code={code} traceId={traceId} />
          </div>
          {hasEnhancedErrorsPermission && (
            <div
              style={{
                width: '100%',
                marginBottom: 'var(--brz-spacing-2)',
                marginTop: 'var(--brz-spacing-2)',
              }}
            >
              <Heading
                size="base"
                style={{ marginBottom: 'var(--brz-spacing-2)' }}
              >
                More information
              </Heading>
              <InputCode
                value={error.stack ?? error.message}
                name="errorMessage"
                size="full"
                mode="json"
                height={6}
                readonly
                hideLineNumbers
              />
            </div>
          )}
        </Column>
      </ModalBody>
      <ModalFooter>
        <ButtonGroup slot="right">
          <Button buttonType="transparent" onClick={handleDismiss}>
            Close
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </Modal>
  )
}

export default ErrorDialog
